import type { Modifier } from '@dnd-kit/core';

/**
 * Custom modifier to center the drag preview under the cursor
 * instead of positioning its top-left corner at the cursor.
 *
 * This modifier calculates the offset from where the user clicked
 * on the draggable element and positions the preview so its center
 * aligns with the cursor position.
 *
 * @param PREVIEW_WIDTH - Width of the DragPreview component (180px)
 * @param PREVIEW_HEIGHT - Height of the DragPreview component (150px)
 */
export const createCenterCursorModifier =
  (PREVIEW_WIDTH: number, PREVIEW_HEIGHT: number): Modifier =>
  ({ activatorEvent, draggingNodeRect, transform }) => {
    // Get cursor position from the activator event
    if (
      activatorEvent &&
      'clientX' in activatorEvent &&
      'clientY' in activatorEvent
    ) {
      const cursorX = activatorEvent.clientX as number;
      const cursorY = activatorEvent.clientY as number;

      // Calculate offset from draggable element's position to cursor
      const offsetX = draggingNodeRect
        ? cursorX - draggingNodeRect.left
        : PREVIEW_WIDTH / 2;
      const offsetY = draggingNodeRect
        ? cursorY - draggingNodeRect.top
        : PREVIEW_HEIGHT / 2;

      // Position preview so its center is at the cursor
      return {
        ...transform,
        x: transform.x + offsetX - PREVIEW_WIDTH / 2,
        y: transform.y + offsetY - PREVIEW_HEIGHT / 2,
      };
    }

    // Fallback: center from current transform position
    return {
      ...transform,
      x: transform.x - PREVIEW_WIDTH / 2,
      y: transform.y - PREVIEW_HEIGHT / 2,
    };
  };

/**
 * Pre-configured modifier for DragPreview (180x150)
 */
export const centerCursorModifier = createCenterCursorModifier(180, 150);
